class MenuComponent extends HTMLElement {
    static menuDataPromise; // Cache promise here
  
    constructor() {
      super();
      this.side = this.getAttribute('side') || 'leftMenu'; // use 'side' attribute to determine left or right
      this.createSkeleton();
    }
  
    connectedCallback() {
      this.updateMenu();
    }
  
    createSkeleton() {
      this.skeleton = document.createElement('ul');
      this.appendChild(this.skeleton);
      for (let i = 0; i < 4; i++) {  // Assuming 4 is the usual number of menu items.
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'skeleton-link';
        li.appendChild(a);
        this.skeleton.appendChild(li);
      }
    }
  
    async fetchMenuData() {
      // Re-use cached promise if it exists
      if (MenuComponent.menuDataPromise) {
        return MenuComponent.menuDataPromise;
      }
      // Else fetch data and store promise
      MenuComponent.menuDataPromise = fetch('/?view=navigation').then(response => response.json());
      return MenuComponent.menuDataPromise;
    }
  
    async updateMenu() {
      try {
        const data = await this.fetchMenuData();
        this.skeleton.innerHTML = ''; // Clear the skeleton links
        this.renderMenu(data[this.side]);
      } catch (err) {
        console.error('An error occurred while fetching the menu:', err);
      }
    }
  
    renderMenu(links) {
      const ul = this.querySelector('ul');
  
      // Clear any existing items first
      ul.innerHTML = '';
  
      links.forEach(link => {
          const li = document.createElement('li');
          const a = document.createElement('a');
  
          // Set the properties of the anchor element
          a.textContent = link.title;
          a.href = link.url;
          li.dataset.mobile = link.title.toLowerCase().replace(/\s+/g, "-");
          a.title = link.title.replace(' ', '-').toLowerCase();
  
          li.appendChild(a);
          ul.appendChild(li);
  
          const hoverMenu = this.querySelector(`.mega-menu[data-for="${link.title.replace(' ', '-').toLowerCase()}"]`);
  
          if (hoverMenu) {
              li.addEventListener('mouseenter', () => {
                  this.showHoverMenu(hoverMenu)
              });
  
                li.addEventListener('click', (e) => {
                  this.showHoverMenu(hoverMenu)
                  e.target.classList.add('active');
              });

              li.addEventListener('mouseleave', () => {
                  hoverMenu.addEventListener('mouseenter', () => {
                      this.showHoverMenu(hoverMenu);
                  });
  
                  hoverMenu.addEventListener('mouseleave', () => {
                      this.hideHoverMenu(hoverMenu);
                  });
  
                  if (!hoverMenu.matches(':hover')) {
                      this.hideHoverMenu(hoverMenu);
                  }
              });
          }
      });
  }
  
  showHoverMenu(hoverMenu) {
    hoverMenu.classList.add('show');  /* Use class to control display and animate */
}

hideHoverMenu(hoverMenu) {
    hoverMenu.classList.remove('show');
}
  }
  
  customElements.define('kos-menu', MenuComponent);
  
  class Slider extends HTMLElement {
    constructor() {
      super();
  
      this.defaultOptions = {
        perView: 1,
        perViewTablet: this.perView,
        perViewMobile: this.perView,
        TRANSITION_TIMING: 5000,
        MOBILE_BREAKPOINT: 767,
        TABLET_BREAKPOINT: 1023,
      };
  
      this.initializeAttributes();
      this.currentSlide = 0;
    }
  
    /**
     * Called when the custom element is connected to the DOM. Initializes the slider component.
     */
     connectedCallback() {
      this.autoLoop = this.getAttribute('auto-loop') === 'true';
      this.cacheElements();
      this.calculateSlideWidths();
      this.initializeFeatures();
      this.addEventListeners();
      this.attachResizeHandler();
    }
  
    /**
     * Called when the custom element is disconnected from the DOM. Cleans up timeouts to prevent memory leaks.
     */
    disconnectedCallback() {
        clearTimeout(this.autoLoopTimeout);
        clearTimeout(this.backgroundColorTimeout);
        window.removeEventListener('resize', this.resizeHandler);
    }

      /**
       * @info Initializes the attributes for the slider component using the provided options or default options.
       */
       initializeAttributes() {
        this.perView = parseInt(this.getAttribute('per-view')) || this.defaultOptions.perView;
        this.perViewTablet = parseInt(this.getAttribute('per-view-tablet')) || this.defaultOptions.perViewTablet;
        this.perViewMobile = parseInt(this.getAttribute('per-view-mobile')) || this.defaultOptions.perViewMobile;
  
        this.TRANSITION_TIMING = parseInt(this.getAttribute('transition-timing')) || this.defaultOptions.TRANSITION_TIMING;
      
        this.MOBILE_BREAKPOINT = this.defaultOptions.MOBILE_BREAKPOINT;
        this.TABLET_BREAKPOINT = this.defaultOptions.TABLET_BREAKPOINT;
  
        this.infiniteLoop = this.getAttribute('infinite-loop') === 'true';
        const autoLoopAttribute = this.getAttribute('auto-loop');
        this.autoLoop = autoLoopAttribute === 'true'; // Will be true if attribute is 'true', false otherwise

        this.dynamicBackground = this.getAttribute('dynamic-background') === 'true';
        this.dotsEnabled = this.getAttribute('dots') === 'true';
        this.arrows = this.getAttribute('arrows') === 'true';
      }
  
      /**
       * Caches DOM elements for the slider component.
       */
       cacheElements() {
        this.sliderContainer = this.querySelector('.slider-container');
        this.slides = Array.from(this.querySelectorAll('.slide'));
      }
  
      /**
       * Initializes the features for the slider component based on the attributes.
       * @returns {void}
       */
      initializeFeatures() {
        if (this.dotsEnabled) this.initDots();
        if (this.autoLoop) this.startAutoLoop();
        if (this.arrows) this.initArrows();
      }
  
      /**
       * Adds mouse and touch event listeners to the slider container.
       * For touch devices, the 'touchmove' event is set as passive to improve scrolling performance.
       */
       addEventListeners() {
        // Mouse events
        this.sliderContainer.addEventListener('mousedown', (e) => this.startDrag(e));
        this.sliderContainer.addEventListener('mousemove', (e) => this.drag(e));
        this.sliderContainer.addEventListener('mouseup', (e) => this.endDrag(e));
        this.sliderContainer.addEventListener('mouseleave', (e) => this.endDrag(e));
  
        // Touch events
        this.sliderContainer.addEventListener('touchstart', (e) => this.startDrag(e), { passive: false });
        this.sliderContainer.addEventListener('touchmove', (e) => this.drag(e), { passive: true });
        this.sliderContainer.addEventListener('touchend', (e) => this.endDrag(e));
      }
  
      /**
       * @info Calculates slide widths based on the viewport width and sets the slide width for each slide.
       * @returns {void}
       */
      calculateSlideWidths() {
        let parentWidth = this.parentElement.offsetWidth;
    
        if (parentWidth >= 3840) {
            parentWidth = 3840;
        }
    
        const viewportWidth = window.innerWidth;
    
        this.initializeAttributes(); // Initialize attributes before using them
    
        if (viewportWidth <= this.MOBILE_BREAKPOINT) {
            this.perView = this.perViewMobile;
        } else if (viewportWidth <= this.TABLET_BREAKPOINT) {
            this.perView = this.perViewTablet;
        } else {
            this.perView = parseInt(this.getAttribute('per-view')) || 1;
        }
    
        this.slideWidth = parentWidth / this.perView;
    
        this.slides.forEach(slide => slide.style.width = `${this.slideWidth}px`);
        // void this.sliderContainer.offsetWidth;

        this.updateSliderTransform();
    }

    handleArrowClick(direction) {
      if (direction === 'left') {
        this.navigate(-1); // Move one slide to the left
      } else if (direction === 'right') {
        this.navigate(1);  // Move one slide to the right
      }
    }
  
      /**
       * Starts the auto loop for the slider.
       * If the `auto-loop` attribute is not present or set to "false", the function will not execute.
       * Otherwise, it will update the slide position and start a new timeout for the next loop.
       * @function
       * @returns {void}
       */
       startAutoLoop() {
        // Check if auto loop is enabled
        if (!this.autoLoop) return;
  
        // Set a timeout for the next auto loop iteration
        this.autoLoopTimeout = setTimeout(() => {
          // Update the current slide based on whether or not infinite loop is enabled
          this.currentSlide++;
          if (this.infiniteLoop) {
            if (this.currentSlide > this.slides.length - this.perView) {
              this.currentSlide = 0;
            }
          } else {
            this.currentSlide = Math.min(this.currentSlide, this.slides.length - this.perView);
          }
  
          // Update the slide position and dots
          requestAnimationFrame(() => {
            this.sliderContainer.style.transition = 'transform 0.5s ease-out';
            this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth}px)`;
            this.updateDots();
          });
  
          // Call updateBackgroundColor after the transition has finished
          this.backgroundColorTimeout = setTimeout(() => {
            this.updateBackgroundColor();
          }, 500); // 0.5s transition duration
  
          // Start the next auto loop iteration
          this.startAutoLoop();
        }, this.TRANSITION_TIMING);
      }
  
      /**
       * Start drag event handler
       * @info Adds the 'grabbing' class to the slider container and sets up
       *       the necessary properties to track the drag movement
       * @param {MouseEvent|TouchEvent} e - The event object
       * @return {void}
       */
       startDrag = (e) => {
        // Clear auto loop timeout
        clearTimeout(this.autoLoopTimeout);
  
        // Set initial drag position
        this.startX = (e.clientX || e.touches[0].clientX);
  
        // Add grabbing class to slider container
        this.sliderContainer.classList.add('grabbing');
  
        // Set dragging flag to true
        this.dragging = true;
        this.startY = (e.clientY || e.touches[0].clientY);
      }
  
      /**
       * Initializes the dots container and adds each dot element to it.
       * Attaches a click event listener to each dot that triggers the goToSlide method.
       * @function
       * @memberof Slider
       * @returns {void}
       */
      initDots() {
        // Create the container for the dots
        this.dotsContainer = document.createElement('div');
        this.dotsContainer.classList.add('dots-container');
        this.appendChild(this.dotsContainer);
      
        // Initialize the array of dot elements
        this.dots = [];
      
        const useKosDots = this.getAttribute('dots-style') === 'kos'; // Check for the "kos" attribute
        
        // Create a dot element for each slide
        this.slides.forEach((slide, index) => {
          let dot;
          if (useKosDots) {
            // Create SVG element and set its attributes and innerHTML
            dot = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            dot.setAttribute("width", "20px");
            dot.setAttribute("height", "20px");
            dot.setAttribute("viewBox", "0 0 43.000000 41.000000");
            dot.classList.add('dot');
            dot.innerHTML = `
              <g transform="translate(0.000000,41.000000) scale(0.100000,-0.100000)" stroke="none">
                <path d="M140 391 c-70 -27 -89 -43 -112 -91 -30 -61 -29 -150 1 -212 30 -63
                         81 -88 177 -88 98 1 144 24 183 92 39 67 47 109 32 160 -14 44 -79 121 -122
                         144 -37 19 -104 17 -159 -5z"></path>
              </g>
            `;
          } else {
            dot = document.createElement('div');
            dot.classList.add('dot');
          }
          
          if (index === this.currentSlide) {
            dot.classList.add('active');
          }
          dot.addEventListener('click', () => this.goToSlide(index));
          this.dotsContainer.appendChild(dot);
      
          // Add the dot element to the array
          this.dots.push(dot);
        });
      }
  
      /**
       * Update the active dot indicator.
       * @function
       * @memberof Slider
       * @info Updates the class of the active dot indicator according to the current slide.
       * @returns {void}
       */
       updateDots() {
        // If dots are not enabled or dots array is empty, exit the function.
        if (!this.dotsEnabled || !this.dots || !this.dots.length) return;
  
        // Loop through each dot element and update its active class.
        this.dots.forEach((dot, index) => {
          if (index === this.currentSlide) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
  
      /**
       * Goes to a specific slide by its index.
       * @function
       * @memberof Slider
       * @param {number} slideIndex - The index of the slide to go to.
       * @returns {void}
       */
       goToSlide(slideIndex) {
        // Set the current slide to the specified slide index
        this.currentSlide = slideIndex;
  
        // Request an animation frame to run the slide transition with a smooth animation
        requestAnimationFrame(() => {
          // Set the slider container's transition and transform CSS properties to slide to the current slide
          this.sliderContainer.style.transition = 'transform 0.5s ease-out';
          this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth}px)`;
  
          // Update the background color and dots for the new slide
          this.updateBackgroundColor();
          this.updateDots();
        });
      }
  
      /**
       * Handle drag event for the slider container.
       * @function
       * @memberof Slider
       * @param {Event} e - The event object.
       * @returns {void}
       */
       drag = (e) => {
        if (!this.dragging) return; // Exit if not currently dragging

        const y = e.pageY || e.touches[0].pageY;
        const dy = y - this.startY;
  
        // Calculate the change in X position from the starting position
        const x = e.pageX || e.touches[0].pageX;
        const dx = x - this.startX;

        if (Math.abs(dx) > Math.abs(dy)) {
          e.preventDefault();

          // Update the position of the slider container
          requestAnimationFrame(() => {
            this.sliderContainer.style.transition = 'none'; // Remove any existing transition
            this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth + dx}px)`; // Move the container
          });
        }
      };
  
      /**
       * Initializes the arrow buttons and adds event listeners for navigation.
       * @function
       * @memberof Slider
       * @returns {void}
       */
      initArrows() {
        // Create the container for the arrows (if not already done)
        this.arrowsContainer = document.createElement('nav');
        this.appendChild(this.arrowsContainer);
      
        const useKosArrows = this.getAttribute('arrow-style') === 'kos'; // Check for the "kos" style
      
        ['left', 'right'].forEach((direction) => {
          let arrow;
          if (useKosArrows) {
            // Create SVG element for arrow
            arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            arrow.setAttribute("width", "29px");
            arrow.setAttribute("height", "25px");
            arrow.setAttribute("viewBox", "0 0 29.000000 25.000000");
            arrow.classList.add('arrow', `arrow-${direction}`);
            arrow.innerHTML = `
              <g transform="translate(0.000000,25.000000) scale(0.100000,-0.100000)" stroke="none">
                <path d="M57 192 c-31 -32 -57 -62 -57 -68 0 -5 26 -35 58 -67 45 -45 60 -55 70 -45 18 18 15 29 -15 60 l-27 28 96 0 c78 0 97 3 102 16 11 29 -4 34 -101 34 l-97 0 28 29 c27 28 32 61 11 68 -6 2 -36 -23 -68 -55z"></path>
              </g>
            `;
          } else {
            arrow = document.createElement('div');
            arrow.classList.add('arrow', `arrow-${direction}`);
          }
          
          // Attach click event or any other logic
          arrow.addEventListener('click', () => this.handleArrowClick(direction));
      
          this.arrowsContainer.appendChild(arrow);
        });
      }
  
      /**
       * Navigates to the previous or next slide based on the direction given
       * @function
       * @memberof Slider
       * @param {number} direction - The direction to navigate, either -1 for previous or 1 for next
       * @returns {void}
       */
       navigate(direction) {
        this.currentSlide += direction;
  
        // Handle infinite loop mode
        if (this.infiniteLoop) {
          if (this.currentSlide < 0) {
            this.currentSlide = this.slides.length - this.perView;
          } else if (this.currentSlide > this.slides.length - this.perView) {
            this.currentSlide = 0;
          }
        }
        // Handle regular mode
        else {
          this.currentSlide = Math.min(Math.max(this.currentSlide, 0), this.slides.length - this.perView);
        }
  
        // Update the slide position and dots
        requestAnimationFrame(() => {
          this.sliderContainer.style.transition = 'transform 0.5s ease-out';
          this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth}px)`;
          this.updateBackgroundColor();
          this.updateDots();
        });
      }
  
      /**
       * Updates the background color of the slider's parent element if `dynamic-background` attribute is set to true and `perView` is 1.
       * The background color is taken from the `data-bg-color` attribute of the slide at the current slide index.
       * @function
       * @memberof Slider
       * @returns {void}
       */
       updateBackgroundColor() {
        // Return early if dynamic background is disabled or perView is not 1
        if (!this.dynamicBackground || this.perView !== 1) return;
  
        // Get the slide at the current slide index
        const nextSlide = this.slides[this.currentSlide];
  
        // Get the background color from the data-bg-color attribute
        const bgColor = nextSlide.getAttribute('data-bg-color');
  
        // If a background color is found, apply it to the parent element with a transition
        if (bgColor) {
          this.style.transition = 'background-color 0.5s ease-out';
          this.style.backgroundColor = bgColor;
        }
      }
  
      /**
       * Attach an event listener to the window resize event and recalculate slide widths and update slider transform on resize.
       * @function
       * @memberof Slider
       */
      attachResizeHandler() {
        this.resizeHandler = () => {
            this.calculateSlideWidths();
            this.updateSliderTransform();
        };
        window.addEventListener('resize', this.resizeHandler);
    }
  
      /**
      * Updates the transform property of the sliderContainer to reflect the current slide position
      * after a window resize event.
      * @function
      * @memberof Slider
      */
      updateSliderTransform() {
        requestAnimationFrame(() => {
          this.sliderContainer.style.transition = 'none';
          this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth}px)`;
        });
      }
  
     /**
       * This function is called when the user has finished dragging the slider.
       * It calculates the distance and direction of the drag and updates the current slide accordingly.
       * Then it applies the necessary CSS transforms to move the slider to the updated slide.
       * @function
       * @memberof Slider
       * @param {Event} event - The drag event object
       * @return {void}
       */
       endDrag = (event) => {
        // If the user is not dragging, do nothing
        if (!this.dragging) return;
  
        // Get the current position of the pointer and calculate the distance of the drag
        const { pageX, changedTouches } = event;
        const currentX = pageX || (changedTouches && changedTouches[0].pageX) || this.startX;
        const distanceX = currentX - this.startX;
  
        // Remove the grabbing class from the slider container and set dragging to false
        this.sliderContainer.classList.remove('grabbing');
        this.dragging = false;
  
        // Add a threshold for triggering a swipe
        const SWIPE_THRESHOLD = 50;
        if (Math.abs(distanceX) < SWIPE_THRESHOLD) {
          // If the swipe distance is less than the threshold, start the auto loop and do nothing
          this.startAutoLoop();
          return;
        }
  
        // Calculate the new current slide index based on the direction of the drag
        this.currentSlide += distanceX < 0 ? 1 : -1;
  
        // Update the current slide index to allow for infinite looping or keep it within the bounds
        this.updateCurrentSlideIndex();
  
        // Apply the necessary CSS transforms to move the slider to the updated slide
        requestAnimationFrame(() => {
          this.sliderContainer.style.transition = 'transform 0.5s ease-out';
          this.sliderContainer.style.transform = `translateX(${-this.currentSlide * this.slideWidth}px)`;
          this.updateDots();
        });
  
        // Update the slider background color based on the new current slide
        this.updateBackgroundColor();
        // Start the auto loop
        this.startAutoLoop();
      }
  
      /**
       * Updates the current slide index based on whether infinite looping is enabled.
       * Ensures that the current slide index stays within the bounds of the slider when infinite looping is disabled.
       * @function
       * @memberof Slider
       * @return {void}
       */
       updateCurrentSlideIndex() {
        if (this.infiniteLoop) {
          // Update the current slide index for infinite looping
          if (this.currentSlide < 0) {
            this.currentSlide = this.slides.length - this.perView;
          } else if (this.currentSlide > this.slides.length - this.perView) {
            this.currentSlide = 0;
          }
        } else {
          // Ensure that the current slide index stays within the bounds of the slider
          this.currentSlide = Math.min(Math.max(this.currentSlide, 0), this.slides.length - this.perView);
        }
      }
  }
  
  customElements.define('kos-slider', Slider);